/*
 * Jira Ticket:
 * Created Date: Thu, 19th Nov 2020, 16:06:48 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { Fragment, useEffect, useState } from 'react';
import { SimpleForm, useNotify, useRedirect, Notification } from 'react-admin';
import { MultiLanguageToolbar } from 'Components/Toolbars';
import useDataProviderWrapper from 'hooks/dataProviderWrapper';
import FormChildren from './FormChildrenWithProps';
import recursiveMap from 'utils/recursiveMapReactChildren';
import { ReactAdminNotifyError } from 'utils/errors';
import { validateNoMissingLocaleFields } from './validateForm';
import addLanguageInput from './addLanguageInput';
import { languageNames, supportedLanguages } from 'utils';

const enLanguageNames = languageNames(); // default is en

const MultiLanguageForm = ({
  additionalToolbarComponents = null,
  excludedSources = [],
  defaultLang = 'en',
  deleteRedirect,
  hideDelete,
  extendToolbar = 0,
  sourcesToSkipRecursion,
  onSuccess = () => {},
  onFailure = () => {},
  doRedirect = false,
  requiredLanguages = [], // use this to determine which languages are required (besides the default language)
  allowLanguageRemoval = true,
  disableToolbar = false,
  cleanOrphaned = true, // cleans out languages that have not yet been set
  childref,
  validateForm = () => {},
  enableNotification = false,
  createRecord = {},
  ...props
}) => {
  const { disabled, children, resource, record, redirect } = props;
  const callToDataProvider = useDataProviderWrapper();
  const notify = useNotify();
  const redirectTo = useRedirect();
  const [languages, setLanguages] = useState([]);
  const [language, setLanguage] = useState(defaultLang);
  const [localisations, setLocalisations] = useState(record.localisations); // an array of localisations [{language: 'en', ...fields}, {...localisation}]
  const [currentRecord, setCurrentRecord] = useState(record);
  const [saving, setSaving] = useState(false);
  const [invalid, setInvalid] = useState(false);

  // initialise the language input
  useEffect(() => {
    const initialiseLanguageInput = () => {
      if (supportedLanguages) {
        const supportedLanguagesWithCodes = supportedLanguages.map(
          (language) => {
            return {
              id: language,
              code: language,
              name: enLanguageNames.of(language),
            };
          }
        );
        setLanguages(supportedLanguagesWithCodes);
      }
    };
    initialiseLanguageInput();
  }, [supportedLanguages]);

  const childrenExtended = addLanguageInput({
    children,
    defaultLang,
    languages,
    disabled,
    allowLanguageRemoval,
  });

  const sources = [];
  recursiveMap(
    childrenExtended,
    (child) => {
      if (
        child.props &&
        child.props.source &&
        !excludedSources.includes(child.props.source)
      ) {
        return sources.push(child.props.source);
      }
    },
    sourcesToSkipRecursion
  );

  const formProps = {
    children: childrenExtended,
    defaultLang,
    excludedSources,
    language,
    setLanguage,
    localisations,
    setLocalisations,
    currentRecord,
    setInvalid,
    sources,
    sourcesToSkipRecursion,
    childref,
  };

  // let SimpleForm know which localisation we are using
  let languageProps = { ...props };

  if (localisations && localisations.length > 0) {
    const localisation = localisations.find(
      (localisation) => localisation.language === language
    );
    languageProps.record = {
      ...localisation,
    };
    if (record.id) {
      languageProps.record.id = record.id;
      languageProps.id = record.id;
    }
  } else {
    const localisationsArray = [];
    // initialise the form data with the default lang
    const newLocalisation = sources.reduce(
      (o, key) => ({
        ...o,
        [key]: key === 'language' ? defaultLang : currentRecord[key] || null,
      }),
      {}
    );
    localisationsArray.push(newLocalisation);

    // build the form data
    const allSources = ['localisations'];
    recursiveMap(
      childrenExtended,
      (child) => {
        if (child.props && child.props.source) {
          return allSources.push(child.props.source);
        }
      },
      sourcesToSkipRecursion
    );

    // create the localisations for the required languages (not just english)
    for (let i = 0; i < requiredLanguages.length; i++) {
      const requiredLanguage = requiredLanguages[i];
      if (requiredLanguage !== defaultLang) {
        const newRequiredLocalisation = sources.reduce(
          (o, key) => ({
            ...o,
            [key]:
              key === 'language'
                ? requiredLanguage
                : currentRecord[key] || null,
          }),
          {}
        );
        localisationsArray.push(newRequiredLocalisation);
      }
    }

    const newRecord = allSources.reduce(
      (o, key) => ({
        ...o,
        [key]:
          key === 'localisations'
            ? { ...newLocalisation }
            : key === 'language'
            ? newLocalisation.language
            : currentRecord[key] || null,
      }),
      {}
    );

    setLocalisations(localisationsArray);
    setCurrentRecord({
      ...newRecord,
    });
  }

  const handleMultiLanguageSubmit = (data) => {
    try {
      data.localisations = localisations;
      const invalidLocaleFields = validateNoMissingLocaleFields({
        children: childrenExtended,
        excludedSources,
        localisations,
        sourcesToSkipRecursion,
        requiredLanguages,
      });
      if (invalidLocaleFields && invalidLocaleFields.language) {
        throw new ReactAdminNotifyError({
          message: 'error.form.multiLang.missingField',
          payload: invalidLocaleFields,
          undoable: false,
          timeout: 10000,
        });
      }
      setSaving(true);

      return callToDataProvider({
        type: record && record.id ? 'UPDATE' : 'CREATE',
        resource,
        payload: {
          id: data.id || null,
          data: {
            ...createRecord, // add in any predefined values - these will be overwritten if they clash with the form
            ...data,
          },
        },
        onSuccess: (result) => {
          setSaving(false);
          // perform any passed onSuccess functions e.g. refreshView
          onSuccess(result);
          if (doRedirect) {
            // handle the redirection request
            let redirectionPath = `/${resource}`;
            if (redirect === 'edit') {
              redirectionPath += `/${result.data.id}`;
            } else if (redirect === 'show') {
              redirectionPath += `/${result.data.id}/show`;
            } else {
              // assume custom path
              redirectionPath += redirect;
            }
            redirectTo(redirectionPath);
          }
        },
        onFailure: (error) => {
          setSaving(false);
          // perform any failed sideeffects
          onFailure(error);
        },
      });
    } catch (error) {
      return notify(
        error.message,
        'warning',
        error.payload,
        error.undoable,
        error.timeout
      );
    }
  };

  return !(languages.length > 0 && localisations.length > 0) ? null : (
    <Fragment>
      <SimpleForm
        {...languageProps}
        save={handleMultiLanguageSubmit}
        saving={saving}
        validate={validateForm}
        initialValues={{ ...languageProps.record, ...currentRecord }}
        record={currentRecord}
        toolbar={
          disabled || disableToolbar ? null : (
            <MultiLanguageToolbar
              additionalToolbarComponents={additionalToolbarComponents}
              enableNotification={enableNotification}
              extend={extendToolbar}
              isInvalid={invalid}
              hideDelete={hideDelete}
              deleteRedirect={deleteRedirect}
            />
          )
        }
        initialValuesEqual={() => true} // prevents us losing initialised form values for our reference field
      >
        <FormChildren {...formProps} />
      </SimpleForm>
      <Notification />
    </Fragment>
  );
};

export default MultiLanguageForm;
